import { forwardRef, useId } from 'react';
import type { SharePosterModel } from './workoutShareData';
import type { ShareAssetImages } from './shareAssets';
import PosterScore, { posterScoreWidth } from './PosterScore';

/** The reference's 770 × 1228 card coordinates also define the exported image. */
const SharePoster = forwardRef<SVGSVGElement, { model: SharePosterModel; images: ShareAssetImages }>(
  function SharePoster({ model, images }, ref) {
    const id = useId().replaceAll(':', '');
    const card = id + '-card', blue = id + '-blue', inset = id + '-inset', photo = id + '-photo';
    const grain = id + '-grain', bevel = id + '-bevel', gloss = id + '-gloss', bounds = id + '-bounds';
    const numeric = /^\d+(\.\d+)?$/.test(model.value);
    const valueWidth = numeric ? Math.min(342, posterScoreWidth(model.value)) : 330;
    return <svg ref={ref} className="share-poster" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 770 1228"
      role="img" aria-label="练刻训练分享海报" fontFamily="Microsoft YaHei, PingFang SC, sans-serif">
      <title>练刻分享海报</title><desc>{model.headline}，{model.badge}，{model.value}{model.unit}，{model.exerciseLine}。{model.metrics.map(m => m.value + m.unit).join('，')}。训练主题示意配图。</desc>
      <defs>
        <linearGradient id={card} x1="0" y1="0" x2="1" y2="1">
          <stop stopColor="#f9fcff"/><stop offset=".28" stopColor="#eaf0f6"/>
          <stop offset=".5" stopColor="#f7faff"/><stop offset=".78" stopColor="#e5edf5"/><stop offset="1" stopColor="#f3f8fe"/>
        </linearGradient>
        <radialGradient id={gloss} cx=".36" cy=".28" r=".75">
          <stop stopColor="#ffffff" stopOpacity=".6"/><stop offset="1" stopColor="#ffffff" stopOpacity="0"/>
        </radialGradient>
        <linearGradient id={blue} x1="0" y1="0" x2="1" y2=".8">
          <stop stopColor="#00baff"/><stop offset=".35" stopColor="#0098ff"/><stop offset=".65" stopColor="#0085fc"/>
          <stop offset=".785" stopColor="#079aff"/><stop offset=".80" stopColor="#51c9ff"/><stop offset=".82" stopColor="#008cff"/><stop offset="1" stopColor="#007aff"/>
        </linearGradient>
        <linearGradient id={inset} x1="0" y1="0" x2=".7" y2="1">
          <stop stopColor="#ffffff"/><stop offset=".55" stopColor="#e8eff6"/><stop offset="1" stopColor="#f5f9ff"/>
        </linearGradient>
        <filter id={grain}><feTurbulence type="fractalNoise" baseFrequency=".75" numOctaves="3" seed="11" stitchTiles="stitch"/>
          <feColorMatrix type="saturate" values="0"/><feComponentTransfer><feFuncA type="linear" slope=".065"/></feComponentTransfer>
        </filter>
        <filter id={bevel} x="-10%" y="-10%" width="120%" height="120%">
          <feMorphology in="SourceAlpha" operator="erode" radius="1.5" result="inside"/>
          <feComposite in="SourceAlpha" in2="inside" operator="out" result="rim"/>
          <feGaussianBlur in="SourceAlpha" stdDeviation="1.2" result="soft"/>
          <feSpecularLighting in="soft" surfaceScale="3" specularConstant=".75" specularExponent="18" lightingColor="#c8f8ff" result="light"><feDistantLight azimuth="225" elevation="45"/></feSpecularLighting>
          <feComposite in="light" in2="rim" operator="in" result="edge"/>
          <feComposite in="SourceGraphic" in2="edge" operator="arithmetic" k2="1" k3=".65"/>
        </filter>
        <filter id={id + '-tile-shadow'} x="-20%" y="-30%" width="140%" height="170%"><feDropShadow dx="0" dy="6" stdDeviation="9" floodColor="#658cb4" floodOpacity=".13"/></filter>
        <clipPath id={photo}><rect x="28" y="642" width="714" height="300" rx="28"/></clipPath>
        <clipPath id={bounds}><rect x="1" y="1" width="768" height="1226" rx="37"/></clipPath>
      </defs>
      <g clipPath={`url(#${bounds})`}>
        <rect width="770" height="1228" fill={`url(#${card})`}/>
        <rect width="770" height="1228" fill={`url(#${gloss})`}/>
        <rect width="770" height="1228" filter={`url(#${grain})`}/>
        <image href={images.hero} x="375" y="105" width="375" height="482" preserveAspectRatio="xMidYMid meet"/>
      </g>
      <rect x="1" y="1" width="768" height="1226" rx="37" fill="none" stroke="#c3d3e4" strokeWidth="2"/>
      <rect x="3" y="3" width="764" height="1222" rx="35" fill="none" stroke="#ffffff" strokeOpacity=".85"/>
      <text x="41" y="63" fill="#07183f" fontSize="34" fontWeight="900" fontStyle="italic" stroke="#07183f" strokeWidth=".7">练刻</text>
      <text x="732" y="56" fill="#7b8ca7" fontSize="23" textAnchor="end">{model.date}</text>
      <text x="48" y="163" fill="#07183f" fontSize="68" fontWeight="900" stroke="#07183f" strokeWidth="1.4" paintOrder="stroke" textLength="540" lengthAdjust="spacingAndGlyphs">{model.headline}</text>
      <rect x="50" y="193" width="248" height="54" rx="27" fill="#c2e3ff"/>
      <text x="174" y="232" fill="#0076ff" fontSize="30" fontWeight="800" textAnchor="middle">{model.badge}</text>
      {numeric ? <PosterScore value={model.value} fill={`url(#${blue})`} filter={`url(#${bevel})`}/>
        : <text x="48" y="501" fill={`url(#${blue})`} fontSize="158" fontWeight="900" textLength="330" lengthAdjust="spacingAndGlyphs">{model.value}</text>}
      {model.unit && <text x={67 + valueWidth} y="500" fill="#07183f" fontFamily="Arial, sans-serif" fontSize="100" fontWeight="900">{model.unit}</text>}
      <text x="48" y="571" fill="#07183f" fontSize={model.exerciseLine.length > 18 ? 32 : 44} fontWeight="900" stroke="#07183f" strokeWidth=".8">{model.exerciseLine}</text>
      <text x="50" y="613" fill="#526076" fontSize="28" fontWeight="500" letterSpacing="2">{model.caption}</text>
      <image href={images.photo} x="28" y="642" width="714" height="300" clipPath={`url(#${photo})`} preserveAspectRatio="xMidYMid slice"/>
      <rect x="28" y="642" width="714" height="300" rx="28" fill="none" stroke="#c3d0dd"/>
      {model.metrics.map((metric, index) => {
        const x = 28 + index * 244;
        return <g key={index}>
          <rect x={x} y="959" width="230" height="92" rx="20" fill={`url(#${inset})`} stroke="#ffffff" strokeWidth="2" filter={`url(#${id}-tile-shadow)`}/>
          <text x={x + 115} y="1024" textAnchor="middle" fill="#0076ff" fontWeight="800" fontSize={metric.unit ? 54 : 30}>
            {metric.value}<tspan fontSize="22" fill="#6d819e" fontWeight="500" dx="9">{metric.unit}</tspan>
          </text>
        </g>;
      })}
      <path d="M41 1085 H732" stroke="#a6b7cd" strokeWidth="1.3"/>
      <text x="41" y="1153" fill="#172b50" fontSize="24" fontWeight="800">扫码，一起开练</text>
      <text x="41" y="1183" fill="#7a8da8" fontSize="22">owlbuy.cn</text>
      <rect x="622" y="1100" width="112" height="112" rx="14" fill="#ffffff" stroke="#bcccdf" strokeWidth="1.5"/>
      <image href={images.qr} x="629" y="1107" width="98" height="98" preserveAspectRatio="xMidYMid meet"/>
    </svg>;
  },
);
export default SharePoster;
